"use client";

import { useState, useEffect } from "react";
import styles from "./styles.module.css";
import DatePicker from "../../shared-modules/DatePicker/DatePicker";
import InputField from "../../ui/input";
import NoteAutocompleteTextarea from "../../ui/note-autocomplete";
import { toast } from "react-hot-toast";
import Button from "../../button-v1";
import {
  createMonitoringSession,
  createPatientNote,
  getPatientNotes,
} from "../../../services/patient/details";
import { getPatientLocationId } from "../../../utils/helper";
import { getNoteTemplates } from "../../../services/location";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toUTCISOString } from "../../../utils/dateFormatters";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardBody,
  CardFooter,
} from "../../ui/card";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

import useCurrentTime from "../DatePicker/useCurrentTime";

const MonitoringCard = ({ noteType, caseManager = false, height, title ,hideLabels='false' }) => {
  const curr = useCurrentTime();
  const [selectedDate, setSelectedDate] = useState(null);
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;

  const queryClient = useQueryClient();
  const [hours, setHours] = useState("00");
  const [minutes, setMinutes] = useState("00");
  const [seconds, setSeconds] = useState("00");
  const [notes, setNotes] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [patientId, setPatientId] = useState(() =>
    sessionStorage.getItem("_pt_id")
  );


  const { refetch: refetchNotes } = useQuery(
    ["patient-notes", patientId, noteType, "latest"],
    () => getPatientNotes(noteType, "latest", patientId, locationId),
    {
      enabled: false,
    }
  );

  const noteTemplatesQuery = useQuery(
    ["note_templates", locationId],
    () => getNoteTemplates(locationId),
    { enabled: !!locationId }
  );
  const noteTemplates = noteTemplatesQuery?.data || [];

  const createSessionMutation = useMutation(
    (sessionData) =>
      // Store under the PATIENT's location, not the viewer's current
      // location (SCRUM-157). `locationId` stays current_loc for the note /
      // template lookups above (location-config, not patient-scoped).
      createMonitoringSession(sessionData, patientId, getPatientLocationId()),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["patient-monitoring-sessions"],
        });
        queryClient.invalidateQueries({ queryKey: ["patient-notes"] });
        toast.success("Monitoring session created successfully");
        refetchNotes();
        setHours("00");
        setMinutes("00");
        setSeconds("00");
        setSelectedDate(null);
        setNotes("");
        setSelectedTemplate("");
        // Dispatch trigger-save event to reset DailiesOORTracker in Readings Review
        window.dispatchEvent(new Event("trigger-save"));
      },
    }
  );

  const handleHoursChange = (e) => {
    setHours(e.target.value);
  };

  const handleMinutesChange = (e) => {
    setMinutes(e.target.value);
  };

  const handleSecondsChange = (e) => {
    setSeconds(e.target.value);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleCancelSelect = () => {
    setSelectedDate(null);
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
  };

  const handleSave = () => {
    const seconds = totalSeconds();

    if (seconds <= 0) {
      return toast.error("Add time to save session");
    }

    // if (notes.trim() === "") {
    //   return toast.error("Add note to save session");
    // }

    createSessionMutation.mutate({
      ...(noteType == "ccm_note" && {
        ccm_note: notes,
        total_ccm_seconds: seconds,
        created_at: toUTCISOString(selectedDate || undefined),
      }),
      ...(noteType == "rpm_note" && {
        rpm_note: notes,
        total_rpm_seconds: seconds,
        created_at: toUTCISOString(selectedDate || undefined),
      }),
    });
  };

  const totalSeconds = () => {
    const hoursInSeconds = parseInt(hours) * 3600;
    const minutesInSeconds = parseInt(minutes) * 60;
    const secondsValue = parseInt(seconds);

    return hoursInSeconds + minutesInSeconds + secondsValue;
  };

  useEffect(() => {
    setPatientId(sessionStorage.getItem("_pt_id"));
  }, []);

  return (
    <Card
      className={styles.wrapper}
      style={{
        backgroundColor: caseManager
          ? "#ffffff"
          : noteType == "ccm_note"
            ? "#F1F1F1"
            : "#dfebf6",
        height: height || (caseManager ? "560px" : "auto"),
      }}
    >
      <CardContent>
        <CardHeader>
          <CardTitle>
            {title || (
              (noteType === "ccm_note" ? "CCM" : "RPM") + " - Monitoring"
            )}
          </CardTitle>
        </CardHeader>

        <CardBody>
          <section className={styles.notes_section}>
            <div className={styles.timeInputsContainer}>
              <div
                className={styles.timeInput}
                style={{
                  marginBottom: caseManager ? "20px" : undefined,
                }}
              >
                {/* Time labels always render so the three fields are distinguishable (SCRUM-847) */}
                <InputField
                  name="hours"
                  value={hours}
                  onChange={handleHoursChange}
                  label="Hours"
                  type="number"
                  min={0}
                />
              </div>

              <div className={styles.timeInput}>
                <InputField
                  name="Minutes"
                  value={minutes}
                  onChange={handleMinutesChange}
                  label="Minutes"
                  type="number"
                  min={0}
                />
              </div>

              <div className={styles.timeInput}>
                <InputField
                  type="number"
                  name="Seconds"
                  value={seconds}
                  onChange={handleSecondsChange}
                  label="Seconds"
                  min={0}
                />
              </div>
            </div>

            <section className={styles.date_picker_container}>
              <div
                className={styles.formGroup}
                style={{
                  marginBottom: caseManager ? "20px" : undefined,
                }}
              >
                {!hideLabels && (
                  <label className={styles.label}>Date</label>
                )}

                <DatePicker
                  selectedDate={selectedDate || curr}
                  onDateSelect={handleDateChange}
                  showTime={true}
                  onCancelSelect={handleCancelSelect}
                />
              </div>
            </section>

            <div
              className={styles.formGroup}
              style={{
                marginBottom: caseManager ? "10px" : undefined,
              }}
            >
              {!hideLabels && (
                <label className={styles.label}>Select note</label>
              )}

              <Select
                value={selectedTemplate}
                onChange={(v) => {
                  if (v) {
                    setNotes((prev) => (prev ? `${prev}\n${v}` : v));
                  } else {
                    // Do not clear notes if empty/none selected, just clear selection
                  }
                  setSelectedTemplate(""); // Reset dropdown
                }}
                disabled={noteTemplatesQuery.isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select note" />
                </SelectTrigger>
                <SelectContent searchField>
                  <SelectItem key="" value="">
                    --None--
                  </SelectItem>
                  {noteTemplates?.map((tpl) => (
                    <SelectItem key={tpl.id} value={tpl.note_template_text}>
                      {tpl.note_template_text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div
              className={styles.formGroup}
              style={{
                marginBottom: caseManager ? "2px" : undefined,
              }}
            >
              {!hideLabels && <label className={styles.label}>Notes</label>}

              <NoteAutocompleteTextarea
                className={styles.textarea}
                placeholder="Enter your notes here ...."
                value={notes}
                onChange={handleNotesChange}
                rows={4}
                style={{
                  backgroundColor: caseManager ? "#F1F1F1" : undefined,
                }}
              />
            </div>
          </section>
        </CardBody>

        <CardFooter>
          <Button
            onClick={() => handleSave()}
            loading={createSessionMutation.isLoading}
          >
            Save
          </Button>
        </CardFooter>
      </CardContent>
    </Card>
  );
};

export default MonitoringCard;